import { defineComponent as _defineComponent } from 'vue'
import { fetchAllColor } from '@/apis/common/materialColor'
import { fetchLblMaterialColor } from '@/apis/lblMaterialColor'
import store from '@/store'
import {
  cacleColorList,
  checkColorIsExist,
  fetchMaterialMatCodes,
} from '@/util/commonFun'
import { cloneDeep } from 'lodash-es'
import { nanoid } from 'nanoid'
import { computed, onMounted, ref } from 'vue'

interface ColorList {
  value: string
  label: string
  children?: ColorList[]
  disabled?: boolean
}
interface MixPaibanSettingsType {
  id: string
  matCode: string
  originTexture: string[]
  targetTexture: string
}
interface PropsType {
  value: {
    enableMixPaiban: boolean
    mixPaibanSettings: MixPaibanSettingsType[]
  }
}

interface MaterialListType {
  label: string
  value: string
  disabled?: boolean
}

interface LblMaterialColorData {
  quality: string[]
  color: string[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'm-paiban-plan',
  props: {
    value: { default: () => ({
    enableMixPaiban: false,
    mixPaibanSettings: [],
  }) }
  },
  emits: ["update:value", "addPlan", "deletePlan", "changeColor", "removeTag"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'update:value', value: any): void
  (e: 'addPlan'): void
  (e: 'deletePlan', index: number): void
  (e: 'changeColor'): void
  (e: 'removeTag'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: {
    enableMixPaiban: boolean
    mixPaibanSettings: MixPaibanSettingsType[]
  } }




const formData = computed({
  get() {
    return new Proxy(props.value, {
      get(target, key) {
        return target[key as keyof typeof target]
      },
      set(target, key, val) {
        target[key as keyof typeof target] = val
        return true
      },
    })
  },
  set(value) {
    emits('update:value', value)
  },
})

// 颜色选择框聚焦
const handleFocusColorSelect = (visible: boolean, index: number) => {
  if (visible) {
    let newColorList = cloneDeep(colorList.value)
    const { mixPaibanSettings } = formData.value
    const { id, matCode } = mixPaibanSettings[index]

    // 获取所有已经使用的颜色
    const usedColors = mixPaibanSettings
      .filter((setting) => setting.id !== id && setting.matCode === matCode)
      .flatMap((setting) => setting.originTexture)

    newColorList = dealColorList(newColorList, usedColors)

    colorList.value = newColorList
  }
}
// 通过递归处理颜色列表
function dealColorList(list: ColorList[], val: string[]): ColorList[] {
  return list.map((item) => {
    const { value, children } = item
    if (children) {
      return { ...item, children: dealColorList(children, val) }
    } else {
      return { ...item, disabled: val.includes(value) }
    }
  })
}

function handleChangeMatCodeVisible(visible: string, index: number) {
  if (visible) {
    const repeatMatCodes = getRepeatMatCodes(index)
    materialList.value = dealMatCodeList(materialList.value, repeatMatCodes)
  }
}
function getRepeatMatCodes(index: number) {
  const { id, originTexture } = formData.value.mixPaibanSettings[index]
  return formData.value.mixPaibanSettings
    .filter(
      (setting) =>
        setting.id !== id &&
        originTexture.some((color) => setting.originTexture.includes(color))
    )
    .map((setting) => setting.matCode)
}
function dealMatCodeList(
  matCodeList: MaterialListType[],
  repeatMatCodes: string[]
) {
  return matCodeList.map((item) => {
    return {
      ...item,
      disabled: repeatMatCodes.includes(item.value),
    }
  })
}

const materialList = ref<Array<MaterialListType>>([])
async function getMaterialList() {
  materialList.value = await fetchMaterialMatCodes(false)
}

const LBL_CABINET_COLOR_KEY = '老板良柜门色卡'

const colorList = ref<Array<ColorList>>([])

function syncCustomColorsToColorList() {
  const customColors = colorList.value.find((item) => item.value === 'custom')
  if (!customColors) return

  const orphanColors = formData.value.mixPaibanSettings
    .flatMap((setting) => setting.originTexture)
    .filter((color) => !checkColorIsExist(color, colorList.value))

  const uniqueColors = [...new Set(orphanColors)]
  customColors.children = uniqueColors.map((color) => ({
    label: color,
    value: color,
  }))
}

async function getColorList() {
  const res = await fetchAllColor()
  let resultList: ColorList[] = []
  if (res && res.status) {
    resultList = cacleColorList(res.data)
    resultList.push({
      value: 'custom',
      label: '自定义',
      children: [],
    })
  }
  colorList.value = resultList
  syncCustomColorsToColorList()
}

function normalizeLblStringList(list: unknown): string[] {
  if (!Array.isArray(list)) return []
  return list
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object') {
        const colorItem = item as {
          value?: string
          label?: string
          name?: string
        }
        return colorItem.value ?? colorItem.label ?? colorItem.name ?? ''
      }
      return ''
    })
    .filter(Boolean)
}

function normalizeLblMaterialColorData(
  data: unknown
): LblMaterialColorData | null {
  if (!data || typeof data !== 'object') return null
  const source = data as { quality?: unknown; color?: unknown }
  return {
    quality: normalizeLblStringList(source.quality),
    color: normalizeLblStringList(source.color),
  }
}

const lblMaterialColorList = ref<LblMaterialColorData>()

function mergeLblMaterialToList() {
  const lblData = lblMaterialColorList.value
  if (!lblData?.quality?.length) return
  const existValues = new Set(materialList.value.map((item) => item.value))
  const lblOptions = Array.from(new Set(lblData.quality))
    .filter((item) => !existValues.has(item))
    .map((item) => ({
      label: item,
      value: item,
    }))
  materialList.value = [...materialList.value, ...lblOptions]
}

function mergeLblColorToList() {
  const lblData = lblMaterialColorList.value
  if (!lblData?.color?.length) return

  const lblChildren = Array.from(new Set(lblData.color)).map((item) => ({
    label: item,
    value: item,
  }))

  const existCatalog = colorList.value.find(
    (item) => item.value === LBL_CABINET_COLOR_KEY
  )
  if (existCatalog) {
    const existValues = new Set(
      (existCatalog.children ?? []).map((child) => child.value)
    )
    const newChildren = lblChildren.filter(
      (child) => !existValues.has(child.value)
    )
    existCatalog.children = [...(existCatalog.children ?? []), ...newChildren]
    return
  }

  colorList.value = [
    ...colorList.value,
    {
      value: LBL_CABINET_COLOR_KEY,
      label: LBL_CABINET_COLOR_KEY,
      children: lblChildren,
    },
  ]
}

async function getLblMaterialColor() {
  const factoryId = sessionStorage.getItem('thinkerx_material_factory_id')
  if (!factoryId || factoryId === 'undefined') return

  try {
    const res = await fetchLblMaterialColor(String(factoryId))
    if (!res?.status) return

    const normalized = normalizeLblMaterialColorData(res.data)
    if (!normalized) return

    lblMaterialColorList.value = normalized
    mergeLblMaterialToList()
    mergeLblColorToList()
    syncCustomColorsToColorList()
  } catch {
    // 老板良色卡接口失败时降级为柜柜色卡
  }
}

// 添加方案
function handleAddPlan() {
  if (!formData.value.mixPaibanSettings?.length) {
    formData.value.mixPaibanSettings = []
  }
  formData.value.mixPaibanSettings.push({
    id: nanoid(),
    matCode: '',
    originTexture: [],
    targetTexture: '',
  })
  formData.value = { ...formData.value }
}
// 删除方案
function handleDeletePlan(index: number) {
  formData.value.mixPaibanSettings.splice(index, 1)
}

const handleChangeColor = () => {
  emits('changeColor')
}
const handleRemoveTag = () => {
  emits('removeTag')
}

// 输入颜色后，回车确认，创建颜色
function handleEnterColor(e: Event, index: number) {
  let { value } = e.target as HTMLInputElement
  // 清除开头和结尾的空格
  value = value.replace(/^\s+|\s+$/g, '')
  if (!value) return
  const isExist = checkColorIsExist(value, colorList.value)
  if (!isExist) {
    const otherColors = colorList.value.find((item) => item.value === 'custom')
    otherColors?.children?.push({ label: value, value })
  }
  formData.value.mixPaibanSettings[index].originTexture.push(value)
}

onMounted(async () => {
  await getMaterialList()
  await getColorList()
  await getLblMaterialColor()
})

return { __sfc: true,props, emits, formData, handleFocusColorSelect, dealColorList, handleChangeMatCodeVisible, getRepeatMatCodes, dealMatCodeList, materialList, getMaterialList, LBL_CABINET_COLOR_KEY, colorList, syncCustomColorsToColorList, getColorList, normalizeLblStringList, normalizeLblMaterialColorData, lblMaterialColorList, mergeLblMaterialToList, mergeLblColorToList, getLblMaterialColor, handleAddPlan, handleDeletePlan, handleChangeColor, handleRemoveTag, handleEnterColor }
}

})